import { useApiMutation, useApiQuery } from "@/api";
import { getCompany } from "@/entities/Company";
import { getMemoTexts } from "@/entities/MemoText/MemoText.queries";
import { generateMemoText as generateMemoTextMutation } from "@/entities/MemoText/MemoText.mutations";
import {
    MemoText,
    MemoTextBackgroundCheckTypeEnum,
    MemoTextCompetitiveAnalysisTypeEnum,
    MemoTextJsonTypeEnum,
    MemoTextMarkdownTypeEnum,
    MemoTextObject,
} from "@/entities/MemoText/MemoText.model";
import { useCallback, useMemo, useState } from "react";

function arrayToTextObject(memoTexts: MemoText[] | undefined): MemoTextObject {
    return {
        [MemoTextMarkdownTypeEnum.COMPANY_SUMMARY]: memoTexts?.find(
            (memoText) =>
                memoText.type === MemoTextMarkdownTypeEnum.COMPANY_SUMMARY,
        ),
        [MemoTextJsonTypeEnum.COMPANY_METADATA]: memoTexts?.find(
            (memoText) =>
                memoText.type === MemoTextJsonTypeEnum.COMPANY_METADATA,
        ),
        [MemoTextCompetitiveAnalysisTypeEnum.COMPETITIVE_ANALYSIS]:
            memoTexts?.find(
                (memoText) =>
                    memoText.type ===
                    MemoTextCompetitiveAnalysisTypeEnum.COMPETITIVE_ANALYSIS,
            ),
        [MemoTextBackgroundCheckTypeEnum.BACKGROUND_CHECK]: memoTexts?.find(
            (memoText) =>
                memoText.type ===
                MemoTextBackgroundCheckTypeEnum.BACKGROUND_CHECK,
        ),
    };
}

export function useMemoText(): {
    text: MemoTextObject;
    generationBuffer: MemoTextObject;
    generateMemoText: (companyWebsite: string | undefined) => void;
    setGenerationBuffer: (generationBuffer: MemoTextObject) => void;
    clearKeyFromBuffer: (key: MemoTextMarkdownTypeEnum) => void;
    isGenerating: boolean;
} {
    const company = useApiQuery(getCompany);
    const memoTexts = useApiQuery(getMemoTexts, {
        params: { companyId: company.data?.uuid || "" },
        enabled: !!company.data?.uuid,
    });
    const [generationBuffer, setGenerationBuffer] = useState<MemoTextObject>(
        {},
    );
    const generateMemoText = useApiMutation(generateMemoTextMutation, {
        onSuccess: (data) => {
            setGenerationBuffer(arrayToTextObject(data));
        },
    });

    const text = useMemo(() => {
        return arrayToTextObject(memoTexts.data);
    }, [memoTexts.data]);

    const clearKeyFromBuffer = useCallback(
        (key: MemoTextMarkdownTypeEnum) => {
            setGenerationBuffer((prev) => {
                const newBuffer = { ...prev };
                delete newBuffer[key];
                return newBuffer;
            });
        },
        [setGenerationBuffer],
    );

    return {
        text,
        generationBuffer,
        generateMemoText: (companyWebsite: string | undefined) => {
            generateMemoText.mutate({ companyWebsite });
        },
        setGenerationBuffer,
        clearKeyFromBuffer,
        isGenerating: generateMemoText.isPending,
    };
}
